import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { type ReactNode } from 'react';

import { ReactComponent as NavinaLogoIcon } from '../../assets/Logo/Logo.svg';
import { theme } from '../../theme/theme';

const HEADER_MIN_BLOCK_SIZE_IN_PX = 80;
const LOGO_SIZE_IN_PX = 30;

// Spinner styling for NA-15196: keep sizing/timing/colors stable across all React loading screens.
// (The inline index.html splash renders only the branded header frame.)
const SPINNER_SIZE_IN_PX = 32;
const SPINNER_BORDER_WIDTH_IN_PX = 3;
const SPINNER_TRACK_COLOR = 'rgba(89, 68, 239, 0.2)';

const Root = styled.div`
	display: flex;
	flex-direction: column;
	inline-size: 100%;
	block-size: 100%;
	overflow: hidden;
	background-color: ${theme.colors.white};
`;

const Header = styled.div`
	flex-shrink: 0;
	display: flex;
	align-items: center;
	column-gap: ${theme.paddings.lg};
	min-block-size: ${HEADER_MIN_BLOCK_SIZE_IN_PX}px;
	padding-inline: ${theme.paddings.lg};
	background-color: ${theme.colors.primary};
	color: ${theme.colors.white};
`;

const LogoIcon = styled(NavinaLogoIcon)`
	inline-size: ${LOGO_SIZE_IN_PX}px;
	block-size: ${LOGO_SIZE_IN_PX}px;
`;

const Body = styled.div`
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	row-gap: ${theme.paddings.base};
`;

const spinAnimation = keyframes`
	to {
		transform: rotate(360deg);
	}
`;

const Spinner = styled.div`
	box-sizing: border-box;
	inline-size: ${SPINNER_SIZE_IN_PX}px;
	block-size: ${SPINNER_SIZE_IN_PX}px;
	border: ${SPINNER_BORDER_WIDTH_IN_PX}px solid ${SPINNER_TRACK_COLOR};
	border-block-start-color: ${theme.colors.purple6};
	border-radius: 50%;
	animation: ${spinAnimation} 800ms linear infinite;

	@media (prefers-reduced-motion: reduce) {
		animation-duration: 2400ms;
	}
`;

export interface BrandedLoadingScreenProps {
	readonly children?: ReactNode;
}

export const BrandedLoadingScreen = observer(function BrandedLoadingScreen({ children }: BrandedLoadingScreenProps) {
	return (
		<Root>
			<Header>
				<LogoIcon aria-hidden />
			</Header>
			<Body>
				<Spinner aria-hidden />
				{children}
			</Body>
		</Root>
	);
});
